// RecurringEventInstances.tsx
import { useState } from 'react';
import { ScrollArea, Group, Text, Box, Flex, Badge } from '@mantine/core';
import { IconRepeat, IconChevronRight } from '@tabler/icons-react';

interface RecurringEventInstance {
  id: number;
  slug: string;
  name: string;
  event_start_date: string;
  event_end_date: string;
  active?: boolean;
  pending?: boolean;
}

interface RecurringEventProps {
  recurringEvent: {
    id: number;
    num_occurrences: number;
    events: RecurringEventInstance[];
  };
  userHasAccess: boolean;
  onEventClick: (event: RecurringEventInstance) => void;
}

export default function RecurringEventInstances({ recurringEvent, userHasAccess, onEventClick }: RecurringEventProps) {
  // Show all events if less than a threshold, otherwise limit with "more" indicator
  const maxVisibleEvents = 10;
  const hasMoreEvents = recurringEvent.events.length > maxVisibleEvents;
  const visibleEvents = recurringEvent.events.slice(0, maxVisibleEvents);
  const remainingCount = recurringEvent.events.length - maxVisibleEvents;

  // Format date functions
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Box mb="md" mt="md" style={{ padding: '10px', backgroundColor: '#17171750', borderRadius: '8px' }}>
      <Flex align="center" mb="xs">
        <Badge
          color="#EA580C"
          size="lg"
          variant="outline"
          style={{
            marginRight: '10px',
            textTransform: 'none',
            fontWeight: 600,
          }}
          leftSection={<IconRepeat size={14} />}
        >
          Recurring Event: {recurringEvent.num_occurrences} Occurrences
        </Badge>
      </Flex>

      <ScrollArea type="always" offsetScrollbars scrollbarSize={6} style={{ width: '100%' }}>
        <div style={{ display: 'flex', paddingBottom: '10px' }}>
          {visibleEvents.map((event) => (
            <Box
              key={event.id}
              onClick={() => onEventClick(event)}
              style={(theme) => ({
                background: 'linear-gradient(145deg, rgba(132, 129, 129, 0.12) 0%, rgba(118, 118, 118, 0.25) 100%)',
                borderRadius: theme.radius.md,
                padding: '12px',
                marginRight: '10px',
                minWidth: '120px',
                cursor: 'pointer',
                textAlign: 'center',
                border:
                  userHasAccess && event.active
                    ? '1px solid #4CAF50'
                    : userHasAccess && event.pending
                      ? '2px solid #FFC107'
                      : '1px solid rgba(128, 128, 128, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(128, 128, 128, 0.2)',
                },
              })}
            >
              {event.event_start_date && (
                <>
                  <Text size="sm" fw={600} style={{ color: '#f2f2f2' }}>
                    {formatDate(event.event_start_date)}
                  </Text>
                  <Text size="xs" style={{ color: '#ccc' }}>
                    {formatTime(event.event_start_date)}
                  </Text>
                </>
              )}
            </Box>
          ))}

          {hasMoreEvents && (
            <Box
              style={(theme) => ({
                backgroundColor: 'rgba(128, 128, 128, 0.15)',
                borderRadius: theme.radius.md,
                padding: '12px',
                minWidth: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(128, 128, 128, 0.25)',
                },
              })}
              onClick={() => {
                // Option to show all events or navigate to the last event
                if (recurringEvent.events.length > maxVisibleEvents) {
                  onEventClick(recurringEvent.events[recurringEvent.events.length - 1]);
                }
              }}
            >
              <Flex direction="column" align="center" justify="center">
                <Text size="md" fw={700} style={{ color: '#f2f2f2' }}>
                  +{remainingCount} more
                </Text>
                <IconChevronRight size={16} color="#ccc" />
              </Flex>
            </Box>
          )}
        </div>
      </ScrollArea>
    </Box>
  );
}
